<template>
  <div class="observation-metadata row justify-content-between">
    <div
      class="col-lg-4 col-md-12 border-bottom mb-2 border-lg-bottom-0 mb-lg-0"
    >
      <h4>Metadata</h4>
      <template v-if="isLoading">
        <dl class="row metadata-list">
          <dt class="col-4 font-weight-bold text-muted">Project</dt>
          <dd class="col-8 font-weight-bold mb-1">
            <span class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
          </dd>

          <dt class="col-4 font-weight-bold text-muted">Datasheet</dt>
          <dd class="col-8 font-weight-bold mb-1">
            <span class="placeholder-glow"
              ><span class="placeholder w-50"></span
            ></span>
          </dd>

          <dt class="col-4 font-weight-bold text-muted">Observer</dt>
          <dd class="col-8 font-weight-bold">
            <span class="placeholder-glow"
              ><span class="placeholder w-50"></span
            ></span>
          </dd>

          <dt class="col-4 font-weight-bold text-muted">Location</dt>
          <dd class="col-8 font-weight-bold">
            <span class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
          </dd>
        </dl>
      </template>

      <dl v-else class="row metadata-list">
        <dt class="col-4 font-weight-bold text-muted">Project</dt>
        <dd class="col-8 font-weight-bold">
          <router-link
            v-if="projectName && projectUrlField"
            :to="{ name: 'ProjectShow', params: { id: projectUrlField } }"
            class="metadata-link"
          >
            {{ projectName }}
          </router-link>
          <span v-else>{{ projectName }}</span>
        </dd>

        <dt class="col-4 font-weight-bold text-muted">Datasheet</dt>
        <dd class="col-8 font-weight-bold">
          <template v-if="datasheetName">
            {{ datasheetName }}
          </template>
          <template v-else>
            bulk upload
            <i
              ref="bulkUploadInfoRef"
              class="fa fa-question-circle ml-2"
              tabindex="0"
            ></i>
            <b-tooltip
              :target="() => $refs.bulkUploadInfoRef"
              placement="auto"
              triggers="hover focus"
            >
              <span
                >This observation does not have a datasheet because it was bulk
                uploaded</span
              >
            </b-tooltip>
          </template>
        </dd>

        <dt class="col-4 font-weight-bold text-muted">Observer</dt>
        <dd class="col-8 font-weight-bold">
          {{ observerName }}
          <span v-if="isCurrentObserver"> (You)</span>
        </dd>

        <dt class="col-4 font-weight-bold text-muted">Location</dt>
        <dd class="col-8 font-weight-bold">
          <router-link
            v-if="locationName && locationId"
            :to="{ name: 'LocationDetails', params: { id: locationId } }"
            class="metadata-link"
          >
            {{ locationName }}
          </router-link>
          <span v-else>{{ locationName }}</span>
        </dd>
        <template v-if="locationLngLat && locationLngLat.exact">
          <dt class="col-4 small">Coordinates Exact</dt>
          <dd class="col-8 small text-muted">
              {{ locationLngLat.exact.lat }},{{ locationLngLat.exact.lng }}
          </dd>
        </template>
        <template v-if="locationLngLat && locationLngLat.blurred">
          <dt class="col-4 small">Coordinates Blurred</dt>
          <dd class="col-8 small text-muted">
              {{ locationLngLat.blurred.lat.toString().replace(/(\.\d{7})\d+/, "$1") }},{{ locationLngLat.blurred.lng.toString().replace(/(\.\d{7})\d+/, "$1") }}
          </dd>
        </template>
        <template v-else-if="locationLngLat">
          <dt class="col-4 small">Coordinates</dt>
          <dd class="col-8 small text-muted">
              {{ locationLngLat.lat }},{{ locationLngLat.lng }}
          </dd>
        </template>
      </dl>
    </div>
    <div
      class="col-lg-5 col-md-8 border-bottom mb-2 border-lg-bottom-0 mb-lg-0"
    >
      <h4>Date Time</h4>
      <template v-if="isLoading">
        <dl class="row metadata-list">
          <dt class="col-5 font-weight-bold text-muted">
            Observation Start Date
          </dt>
          <dd class="col-7 font-weight-bold">
            <span class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
          </dd>

          <dt class="col-5 font-weight-bold text-muted">
            Observation End Date
          </dt>
          <dd class="col-7 font-weight-bold">
            <span class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
          </dd>

          <dt class="col-5 font-weight-bold text-muted">Created Date</dt>
          <dd class="col-7 font-weight-bold">
            <span class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
          </dd>
        </dl>
      </template>

      <dl v-else class="row metadata-list">
        <template v-if="hasValue(startRaw)">
          <dt class="col-5 font-weight-bold text-muted">
            Observation Start Date
          </dt>
          <dd
            class="col-7 font-weight-bold"
            v-b-tooltip.hover.focus
            :title="getObservationTimeTooltip(startRaw, 'Observation began')"
          >
            <span>{{ formatObservationTimestamp(startRaw) }}</span>
            <span
              class="ml-1 small text-muted badge badge-light metadata-definition"
              >local</span
            >
          </dd>
        </template>

        <template v-if="hasValue(endRaw)">
          <dt class="col-5 font-weight-bold text-muted">
            Observation End Date
          </dt>
          <dd
            class="col-7 font-weight-bold"
            v-b-tooltip.hover.focus
            :title="getObservationTimeTooltip(endRaw, 'Observation ended')"
          >
            <span>{{ formatObservationTimestamp(endRaw) }}</span>
            <span
              class="ml-1 small text-muted badge badge-light metadata-definition"
              >local</span
            >
          </dd>
        </template>

        <dt class="col-5 font-weight-bold text-muted">Created Date</dt>
        <dd
          class="col-7 font-weight-bold"
          v-b-tooltip.hover.focus
          :title="getCreatedAtTooltipText(createdAtRaw)"
        >
          {{ formatServerTimestamp(createdAtRaw) }}
          <span
            class="ml-1 small text-muted badge badge-light metadata-definition"
            >{{ localTimeZoneLabel }}</span
          >
        </dd>

        <template v-if="showUpdatedAt">
          <dt class="col-5 font-weight-bold text-muted">Updated Date</dt>
          <dd
            class="col-7 font-weight-bold"
            v-b-tooltip.hover.focus
            :title="getUpdatedAtTooltipText(updatedAtRaw)"
          >
            {{ formatServerTimestamp(updatedAtRaw) }}
            <span
              class="ml-1 small text-muted badge badge-light metadata-definition"
              >{{ localTimeZoneLabel }}</span
            >
          </dd>
        </template>
      </dl>
    </div>
    <div
      v-if="isLoading || hasAnyDurations"
      class="col-lg-3 col-md-4 border-bottom mb-2 border-lg-bottom-0 mb-lg-0"
    >
      <h4>Duration</h4>
      <dl class="row metadata-list">
        <template v-if="isLoading || hasDurationInputValue(travelDuration)">
          <dt class="col-5 col-md-7 font-weight-bold text-muted">Travel</dt>
          <dd class="col-7 col-md-5 font-weight-bold">
            <span v-if="isLoading" class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
            <span v-else>{{
              displayValue(formatMinutesDuration(travelDuration))
            }}</span>
          </dd>
        </template>

        <template v-if="isLoading || hasDurationInputValue(setupDuration)">
          <dt class="col-5 col-md-7 font-weight-bold text-muted">Setup</dt>
          <dd class="col-7 col-md-5 font-weight-bold">
            <span v-if="isLoading" class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
            <span v-else>{{
              displayValue(formatMinutesDuration(setupDuration))
            }}</span>
          </dd>
        </template>
        <template
          v-if="
            isLoading || hasDurationInputValue(getObservationDurationMinutes())
          "
        >
          <dt class="col-5 col-md-7 font-weight-bold text-muted">
            Observation
          </dt>
          <dd class="col-7 col-md-5 font-weight-bold">
            <span v-if="isLoading" class="placeholder-glow"
              ><span class="placeholder w-75"></span
            ></span>
            <span v-else>{{
              displayValue(formatDuration(startRaw, endRaw))
            }}</span>
          </dd>
        </template>
        <template v-if="!isLoading && hasMultipleDurations">
          <dt class="col-5 col-md-7 font-weight-bold text-muted">Total</dt>
          <dd class="col-7 col-md-5 font-weight-bold">
            <span class="border-top pt-1">
              {{ displayValue(formatMinutesDuration(totalDurationMinutes)) }}
            </span>
          </dd>
        </template>
      </dl>
    </div>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "ObservationViewMetadata",

  props: {
    observation: {
      type: Object,
      required: false,
      default: null,
    },
  },

  computed: {
    isLoading() {
      return !this.observation;
    },

    projectName() {
      return this.observation && this.observation.project
        ? this.observation.project.name
        : undefined;
    },

    projectUrlField() {
      return this.observation && this.observation.project
        ? this.observation.project.urlField
        : undefined;
    },

    datasheetName() {
      return this.observation && this.observation.datasheet
        ? this.observation.datasheet.name
        : undefined;
    },

    observerName() {
      return this.observation && this.observation.user
        ? this.observation.user.displayName
        : undefined;
    },

    currentUserId() {
      try {
        const sessionData = JSON.parse(localStorage.getItem("sessionData"));
        return sessionData && sessionData.id ? sessionData.id : undefined;
      } catch (error) {
        return undefined;
      }
    },

    observerId() {
      return this.observation && this.observation.user
        ? this.observation.user.id
        : undefined;
    },

    isCurrentObserver() {
      return (
        this.hasValue(this.currentUserId) &&
        this.currentUserId === this.observerId
      );
    },

    locationName() {
      return this.observation && this.observation.location
        ? this.observation.location.name
        : undefined;
    },

    locationId() {
      return this.observation && this.observation.location
        ? this.observation.location.id
        : undefined;
    },

    locationLngLat() {
      return this.observation &&
        this.observation.lngLat
        ? this.observation.lngLat
        : undefined;
    },

    travelDuration() {
      return this.observation ? this.observation.durationTravel : undefined;
    },

    setupDuration() {
      return this.observation ? this.observation.durationSetup : undefined;
    },

    startRaw() {
      return this.observation ? this.observation.observedAt : undefined;
    },

    endRaw() {
      return this.observation ? this.observation.observationEndedAt : undefined;
    },

    datasheetDateFormat() {
      return this.observation && this.observation.datasheet
        ? this.observation.datasheet.dateFormat
        : undefined;
    },

    createdAtRaw() {
      return this.observation ? this.observation.createdAt : undefined;
    },

    updatedAtRaw() {
      return this.observation ? this.observation.updatedAt : undefined;
    },

    showUpdatedAt() {
      if (!this.hasValue(this.updatedAtRaw)) {
        return false;
      }

      if (!this.hasValue(this.createdAtRaw)) {
        return true;
      }

      return (
        new Date(this.updatedAtRaw).getTime() !==
        new Date(this.createdAtRaw).getTime()
      );
    },

    localTimeZoneLabel() {
      try {
        const parts = new Intl.DateTimeFormat(undefined, {
          timeZoneName: "short",
        }).formatToParts(new Date());
        const timeZonePart = parts.find((part) => part.type === "timeZoneName");
        if (timeZonePart && timeZonePart.value) {
          return timeZonePart.value;
        }

        const options = Intl.DateTimeFormat().resolvedOptions();
        return options && options.timeZone ? options.timeZone : "Local Time";
      } catch (error) {
        return "Local Time";
      }
    },

    totalDurationMinutes() {
      return [
        this.travelDuration,
        this.setupDuration,
        this.getObservationDurationMinutes(),
      ]
        .filter((value) => this.hasValue(value))
        .map((value) => Number(value))
        .filter((value) => !Number.isNaN(value))
        .reduce((sum, value) => sum + value, 0);
    },

    totalDurationCount() {
      return [
        this.travelDuration,
        this.setupDuration,
        this.getObservationDurationMinutes(),
      ].filter((value) => this.hasValue(value) && !Number.isNaN(Number(value)))
        .length;
    },

    hasMultipleDurations() {
      return this.totalDurationCount > 1;
    },

    hasAnyDurations() {
      return (
        this.hasDurationInputValue(this.travelDuration) ||
        this.hasDurationInputValue(this.setupDuration) ||
        this.hasDurationInputValue(this.getObservationDurationMinutes())
      );
    },
  },

  methods: {
    hasValue(value) {
      return value !== undefined && value !== null && value !== "";
    },

    displayValue(value) {
      return this.hasValue(value) ? value : "Unknown";
    },

    getObservationDurationMinutes() {
      if (!this.hasValue(this.startRaw) || !this.hasValue(this.endRaw)) {
        return undefined;
      }

      const start = new Date(this.startRaw);
      const end = new Date(this.endRaw);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return undefined;
      }

      const diffMs = end.getTime() - start.getTime();
      if (diffMs < 0) {
        return undefined;
      }

      return diffMs / 60000;
    },

    hasDurationInputValue(value) {
      if (value === null || value === undefined) {
        return false;
      }

      if (typeof value === "string") {
        const normalized = value.trim();
        if (normalized === "") {
          return false;
        }
      }

      const numeric = Number(value);
      return !Number.isNaN(numeric);
    },

    formatDateTime(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return undefined;
      }

      return date.toLocaleString();
    },

    getObservationTimeTooltip(value, prefix) {
      const formatted = this.formatVerboseLocalTime(value);
      if (!formatted) {
        return `${prefix} is unavailable.`;
      }

      return `${prefix} ${formatted}. This was recorded in the local time zone.`;
    },

    getCreatedAtTooltipText(value) {
      const formatted = this.formatVerboseUtcLocalTime(value);
      if (!formatted) {
        return "Observation creation time is unavailable.";
      }

      return `Observation was submitted to CitSci ${formatted} and was converted to ${this.localTimeZoneLabel} from UTC.`;
    },

    getUpdatedAtTooltipText(value) {
      const formatted = this.formatVerboseUtcLocalTime(value);
      if (!formatted) {
        return "Observation update time is unavailable.";
      }

      return `An update to this observation was submitted to CitSci ${formatted} and was converted to ${this.localTimeZoneLabel} from UTC.`;
    },

    formatVerboseLocalTime(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }
      const rawLocalValue =
        typeof value === "string" ? value.split("+")[0] : value;
      const date = moment(rawLocalValue);
      if (!date.isValid()) {
        return undefined;
      }

      return this.formatVerboseDate(date);
    },

    formatVerboseUtcLocalTime(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }

      const date = moment.utc(value).local();
      if (!date.isValid()) {
        return undefined;
      }

      return this.formatVerboseDate(date);
    },

    formatVerboseDate(date) {
      const day = date.date();
      return `${date.format("dddd MMMM")} ${day}${this.getOrdinalSuffix(day)} ${date.format("YYYY [at] h:mm:ss a")}`;
    },

    getOrdinalSuffix(day) {
      const mod100 = day % 100;
      if (mod100 >= 11 && mod100 <= 13) {
        return "th";
      }

      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    },

    formatObservationTimestamp(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }

      const rawLocalValue =
        typeof value === "string" ? value.split("+")[0] : value;
      const date = moment(rawLocalValue);
      if (!date.isValid()) {
        return undefined;
      }

      const format = this.datasheetDateFormat;

      if (format === "date") {
        return date.format("YYYY/MM/DD");
      }

      if (format === "date_hour_minutes" || format === "start_end_minutes") {
        return date.format("YYYY/MM/DD h:mm A");
      }

      if (format === "datetime" || format === "start_end_seconds") {
        return date.format("YYYY/MM/DD h:mm:ss A");
      }

      return this.formatDateTime(value);
    },

    formatServerTimestamp(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }

      const date = moment.utc(value).local();
      if (!date.isValid()) {
        return undefined;
      }

      const format = this.datasheetDateFormat;

      if (format === "date") {
        return date.format("YYYY/MM/DD");
      }

      if (format === "date_hour_minutes" || format === "start_end_minutes") {
        return date.format("YYYY/MM/DD h:mm A");
      }

      if (format === "datetime" || format === "start_end_seconds") {
        return date.format("YYYY/MM/DD h:mm:ss A");
      }

      return this.formatDateTime(value);
    },

    formatDuration(startValue, endValue) {
      if (!this.hasValue(startValue) || !this.hasValue(endValue)) {
        return undefined;
      }

      const start = new Date(startValue);
      const end = new Date(endValue);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return undefined;
      }

      let diffMs = end.getTime() - start.getTime();
      if (diffMs < 0) {
        return undefined;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [];
      if (days) parts.push(`${days}days`);
      if (hours) parts.push(`${hours} hr`);
      if (minutes) parts.push(`${minutes} min`);
      if (seconds || parts.length === 0) parts.push(`${seconds} s`);

      return parts.join(" ");
    },

    formatMinutesDuration(value) {
      if (!this.hasValue(value)) {
        return undefined;
      }

      const totalMinutes = Number(value);
      if (Number.isNaN(totalMinutes)) {
        return undefined;
      }

      const totalSeconds = Math.round(totalMinutes * 60);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [];
      if (hours) parts.push(`${hours} hr`);
      if (minutes) parts.push(`${minutes} min`);
      if (seconds || parts.length === 0) parts.push(`${seconds} s`);

      return parts.join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
dl {
  margin-bottom: 0 !important;
}
dd {
  margin-bottom: 0.25rem !important;
}
.observation-metadata {
  font-weight: 400;
}

.metadata-label {
  font-weight: 600;
  color: #333;
}

.metadata-value {
  margin: 0;
  word-break: break-word;
}

.metadata-link {
  word-break: break-word;
}

.metadata-definition {
  font-weight: 400;
  line-height: 1.2;
}

.metadata-loading {
  font-size: 0.875rem;
  color: #6c757d;
}

.placeholder {
  display: inline-block;
  min-height: 1em;
  vertical-align: middle;
  cursor: wait;
  background-color: currentColor;
  opacity: 0.2;
  border-radius: 0.25rem;
}

.placeholder-glow .placeholder {
  animation: placeholder-glow 1.5s ease-in-out infinite;
}

@keyframes placeholder-glow {
  50% {
    opacity: 0.35;
  }
}
</style>
